<template>
  <div class="store-selector">
    <!-- Componente Country Selector -->
    <div class="country-selector">
      <div class="select-wrapper">
        <select id="country-select" v-model="localSelectedCountry" @change="onCountryChange" required>
          <option value="" disabled selected class="body20-bold">{{ $t('store.select_country_placeholder') }}</option>
          <option v-for="country in countries" :key="country.code" :value="country.code" class="body20-bold">
            {{ country.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Lista Store -->
    <div class="store-options" v-if="filteredStores.length > 0">
      <div v-for="store in filteredStores" :key="store.id" class="store-card"
        :class="{ selected: isSelectedStore(store) }" @click="selectStore(store)">
        <div class="store-header">
          <div class="legend16-bold">{{ $t('store.official_retailer') }}</div>
          <h3 class="headline30">{{ store.name }}</h3>
        </div>
        <div class="body20-light">
          <p>{{ store.address }}<br>
            {{ store.postcode }}, {{ store.city }}<br>
            {{ getCountryName(store.country) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Notifica quando non ci sono store -->
    <div class="no-stores" v-if="localSelectedCountry && filteredStores.length === 0">
      <appointment-notice v-if="storeNotice" :notice="storeNotice" @accept-notice="acceptNotice" />
      <p v-else>{{ $t('store.no_stores_available') }}</p>
    </div>
  </div>
</template>

<script>
import AppointmentNotice from "./AppointmentNotice.vue";

export default {
  name: "StoreSelector",

  components: {
    AppointmentNotice,
  },

  props: {
    stores: {
      type: Array,
      required: true,
    },
    selectedCountry: {
      type: String,
      default: null,
    },
    currentAppointment: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      localSelectedCountry: this.selectedCountry || "",
      selectedStoreId: null,
      storeNotice: {
        // Usa la chiave di traduzione se disponibile, altrimenti il testo predefinito per compatibilità
        textKey: 'notices.no_stores_in_country',
        text: "Non ci sono punti vendita disponibili nel paese selezionato. Si prega di selezionare un altro paese o contattare il nostro servizio clienti."
      },
    };
  },

  computed: {
    countries () {
      // Ottieni una lista unica di paesi dai negozi
      const uniqueCountries = [...new Set(this.stores.map((store) => store.country))];
      return uniqueCountries.map((code) => {
        return {
          code,
          name: this.getCountryName(code),
        };
      });
    },

    filteredStores () {
      if (!this.localSelectedCountry) return [];

      // Prima filtra per paese
      let result = this.stores.filter(
        (store) => store.country === this.localSelectedCountry
      );

      return result;
    },
  },

  watch: {
    selectedCountry (newValue) {
      this.localSelectedCountry = newValue;
    },
    currentAppointment: {
      handler: 'initializeFromCurrentAppointment',
      immediate: true
    },
    // Aggiungiamo un watch su stores per gestire il caso in cui
    // i negozi vengano caricati dopo il montaggio del componente
    stores: {
      handler: function (newStores) {
        if (newStores.length > 0) {
          this.initializeFromCurrentAppointment();
        }
      },
      immediate: true
    }
  },

  methods: {
    initializeFromCurrentAppointment () {
      if (this.currentAppointment && this.currentAppointment.store && this.stores.length > 0) {
        // Seleziona automaticamente il paese e lo store dell'appuntamento
        const storeId = Number(this.currentAppointment.store.id);
        this.selectedStoreId = storeId;

        // Trova lo store nei dati disponibili
        const selectedStore = this.stores.find(store => Number(store.id) === storeId);

        if (selectedStore) {
          // Imposta il paese
          this.localSelectedCountry = selectedStore.country;
          this.$emit("select-country", this.localSelectedCountry);

          // Emetti l'evento di selezione store
          this.$emit("select-store", selectedStore);
        }
      }
    },

    getCountryName (countryCode) {
      // Usa le traduzioni per i nomi dei paesi
      const translationKey = `countries.${countryCode.toLowerCase()}`;
      const translated = this.$t(translationKey);

      // Se la traduzione esiste e non è uguale alla chiave stessa, usala
      if (translated !== translationKey) {
        return translated;
      }

      // Fallback alla mappa statica per retrocompatibilità
      const countries = {
        IT: "Italia",
        FR: "Francia",
        DE: "Germania",
        UK: "Regno Unito",
        ES: "Spagna",
        // Aggiungi altri paesi secondo necessità
      };

      return countries[countryCode] || countryCode;
    },

    onCountryChange () {
      this.$emit("select-country", this.localSelectedCountry);
    },

    selectStore (store) {
      this.selectedStoreId = Number(store.id);
      this.$emit("select-store", store);
    },

    isSelectedStore (store) {
      // Usa l'ID salvato localmente per verificare se lo store è selezionato
      return this.selectedStoreId === Number(store.id);
    },

    acceptNotice () {
      // Funzione chiamata quando l'utente accetta la notifica
      // Puoi aggiungere qui qualsiasi logica aggiuntiva
    },

    // Metodo per selezionare automaticamente l'unico paese se presente
    autoSelectCountry () {
      if (this.countries.length === 1) {
        this.localSelectedCountry = this.countries[0].code;
        this.onCountryChange();
      }
    },
  },

  mounted () {
    // Quando il componente è montato, verifica se c'è un solo paese o se abbiamo un appuntamento
    this.autoSelectCountry();
    this.initializeFromCurrentAppointment();
  },
};
</script>